import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
    Select,
    Paper,
    Title,
    Text,
    Stack,
    Grid,
    Group,
    Button,
    Table,
    Badge,
    Alert,
} from "@mantine/core";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkOverviewDto {
    name: string;
    collectionName?: string;
    date: string;
    bar: number;
    beer: number;
    water: number;
    wine: number;
    total: number;
}

interface EventBar {
    eventBarId: number;
    name: string;
    isActive: boolean;
}

// ---------------- HELPERS ----------------
const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    if (Array.isArray(result?.value)) return result.value;
    return [];
};

const toNumber = (value: any, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const toSafeDateString = (value: any): string => {
    if (!value) return "";
    const str = String(value);
    return str.length >= 10 ? str.substring(0, 10) : str;
};

const deriveCollectionName = (row: any, resolvedName: string): string => {
    const explicit =
        row?.collectionName ??
        row?.CollectionName ??
        row?.ticketCollectionName ??
        row?.TicketCollectionName;

    if (explicit && String(explicit).trim()) {
        return String(explicit).trim();
    }

    const name = (resolvedName || "").trim();

    if (name.includes(" #")) {
        return name.split(" #")[0].trim();
    }

    return "";
};

const normalizeOverviewRow = (row: any): DrinkOverviewDto => {
    const resolvedName =
        row?.name ??
        row?.Name ??
        row?.personName ??
        row?.PersonName ??
        row?.ticketName ??
        row?.TicketName ??
        "";

    return {
        name: String(resolvedName || "").trim(),
        collectionName: deriveCollectionName(row, resolvedName),
        date:
            row?.date ??
            row?.Date ??
            row?.createdAt ??
            row?.CreatedAt ??
            "",
        bar: toNumber(row?.bar ?? row?.Bar),
        beer: toNumber(row?.beer ?? row?.Beer),
        water: toNumber(row?.water ?? row?.Water),
        wine: toNumber(row?.wine ?? row?.Wine),
        total: toNumber(row?.total ?? row?.Total)
    };
};

// ---------------- COMPONENT ----------------
const AdminDrinkOverview: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [overview, setOverview] = useState<DrinkOverviewDto[]>([]);
    const [bars, setBars] = useState<EventBar[]>([]);

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedBar, setSelectedBar] = useState<string | null>(null);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(null);

    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [availableBars, setAvailableBars] = useState<number[]>([]);
    const [availableNames, setAvailableNames] = useState<string[]>([]);
    const [availableCollectionNames, setAvailableCollectionNames] = useState<string[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ---------------- EVENTS ----------------
    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/events`);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) setEvents(data);
            } catch (err) {
                console.error(err);
            }
        };

        loadEvents();
    }, [apiUrl]);

    // ---------------- LOAD BARS (DB SOURCE OF TRUTH) ----------------
    const loadBars = async (eventId: number) => {
        const res = await fetch(`${apiUrl}/api/EventBars/event/${eventId}`);
        if (!res.ok) return;

        const data = await res.json();
        setBars(Array.isArray(data) ? data : []);
    };

    const getBarName = (barId: number) => {
        return bars.find(b => b.eventBarId === barId)?.name ?? `Bar ${barId}`;
    };

    // ---------------- OVERVIEW ----------------
    const fetchOverview = async (event: number) => {
        if (!event) return;

        try {
            setLoading(true);
            setError(null);

            const res = await fetch(`${apiUrl}/api/drinks/overview?eventId=${event}`);
            if (!res.ok) throw new Error(await res.text());

            const raw = await res.json();
            const rows = toArray<any>(raw).map(normalizeOverviewRow);

            setOverview(rows);

            setAvailableDates(
                Array.from(new Set(rows.map(r => toSafeDateString(r.date)).filter(Boolean))).sort()
            );

            setAvailableBars(
                Array.from(new Set(rows.map(r => r.bar).filter(b => b > 0))).sort((a, b) => a - b)
            );

            setAvailableNames(
                Array.from(new Set(rows.map(r => r.name).filter(Boolean))).sort()
            );

            setAvailableCollectionNames(
                Array.from(new Set(rows.map(r => r.collectionName || "").filter(Boolean))).sort()
            );

        } catch (err: any) {
            setError(err.message);
            setOverview([]);
        } finally {
            setLoading(false);
        }
    };

    // ---------------- EVENT CHANGE ----------------
    useEffect(() => {
        if (!eventId) return;

        setSelectedDate(null);
        setSelectedBar(null);
        setSelectedName(null);
        setSelectedCollectionName(null);

        fetchOverview(eventId);
        loadBars(eventId);
    }, [eventId]);

    // ---------------- FILTER ----------------
    const filteredOverview = useMemo(() => {
        return overview.filter(row => {
            const rowDate = toSafeDateString(row.date);

            return (
                (!selectedDate || rowDate === selectedDate) &&
                (!selectedBar || row.bar === Number(selectedBar)) &&
                (!selectedName || row.name === selectedName) &&
                (!selectedCollectionName || row.collectionName === selectedCollectionName)
            );
        });
    }, [overview, selectedDate, selectedBar, selectedName, selectedCollectionName]);

    // ---------------- TOTALS ----------------
    const totals = useMemo(() => {
        return filteredOverview.reduce((acc, row) => {
            acc.beer += toNumber(row.beer);
            acc.water += toNumber(row.water);
            acc.wine += toNumber(row.wine);
            acc.total += toNumber(row.total);
            return acc;
        }, { beer: 0, water: 0, wine: 0, total: 0 });
    }, [filteredOverview]);

    // ---------------- EXPORT ----------------
    const exportToExcel = () => {
        const exportRows = filteredOverview.map(row => ({
            Navn: row.name,
            Collection: row.collectionName || "",
            Dato: toSafeDateString(row.date),
            Bar: getBarName(row.bar),
            Øl: row.beer,
            Vand: row.water,
            Vin: row.wine,
            Total: row.total
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Drink Overview");

        XLSX.writeFile(
            workbook,
            `drink_overview_${eventId}_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };


    return (

        <div>

            <HeroSectionV2
                titleEn="Bar Overview"
                titleDa="Bar Oversigt"
                descriptionEn="View consumption by person, date, bar, and collection"
                descriptionDa="Se forbrug pr. person, dato, bar og collection"
                imageSrc={Billede1}
            />


            <AdminBackBar
                title="Drinks"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Drinks" }
                ]}
            />



            <Stack
                p="xl"
                gap="xl"
            >
                <Paper
                    shadow="sm"
                    radius="md"
                    p="xl"
                    withBorder
                >


                    <Stack>


                        <Title order={4}>
                            Filtre
                        </Title>



                        <Grid>


                            <Grid.Col
                                span={{
                                    base: 12,
                                    md: 6
                                }}
                            >

                                <Select
                                    label="Event"
                                    placeholder="Vælg event"
                                    value={
                                        eventId
                                            ? String(eventId)
                                            : null
                                    }
                                    onChange={(val) =>
                                        setEventId(
                                            val
                                                ? Number(val)
                                                : 0
                                        )
                                    }
                                    data={
                                        events.map(e => ({
                                            value: String(e.eventID),
                                            label:
                                                e.eventName ||
                                                `Event ${e.eventID}`
                                        }))
                                    }
                                    searchable
                                />


                            </Grid.Col>





                            {
                                availableBars.length > 0 &&

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 6
                                    }}
                                >

                                    <Select
                                        label="Bar"
                                        placeholder="Filtrer på bar"
                                        clearable
                                        value={selectedBar}
                                        onChange={setSelectedBar}
                                        data={
                                            availableBars.map(b => ({
                                                value: String(b),
                                                label: getBarName(b)
                                            }))
                                        }
                                    />

                                </Grid.Col>

                            }


                        </Grid>


                    </Stack>


                </Paper>






                {
                    error &&

                    <Alert
                        color="red"
                        title="Error"
                    >
                        {error}
                    </Alert>

                }







                {
                    !loading &&
                    filteredOverview.length > 0 &&


                    <Paper
                        shadow="sm"
                        radius="md"
                        p="md"
                        withBorder
                    >


                        <Group
                            justify="space-between"
                            mb="md"
                        >


                            <Title order={4}>
                                Overview
                            </Title>


                            <Button
                                onClick={exportToExcel}
                            >
                                Export Excel
                            </Button>


                        </Group>





                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            withColumnBorders
                        >

                            <Table.Thead>

                                <Table.Tr>


                                    <Table.Th>
                                        Navn
                                    </Table.Th>


                                    <Table.Th>
                                        Collection
                                    </Table.Th>


                                    <Table.Th>
                                        Dato
                                    </Table.Th>


                                    <Table.Th>
                                        Bar
                                    </Table.Th>


                                    <Table.Th>
                                        🍺
                                    </Table.Th>


                                    <Table.Th>
                                        🥤
                                    </Table.Th>


                                    <Table.Th>
                                        🍷
                                    </Table.Th>


                                    <Table.Th>
                                        Total
                                    </Table.Th>


                                </Table.Tr>


                            </Table.Thead>





                            <Table.Tbody>


                                {
                                    filteredOverview.map((row, i) => (

                                        <Table.Tr key={i}>


                                            <Table.Td>
                                                {row.name}
                                            </Table.Td>


                                            <Table.Td>
                                                {
                                                    row.collectionName ||
                                                    "-"
                                                }
                                            </Table.Td>


                                            <Table.Td>
                                                {
                                                    toSafeDateString(row.date)
                                                }
                                            </Table.Td>


                                            <Table.Td>
                                                <Badge>
                                                    {
                                                        getBarName(row.bar)
                                                    }
                                                </Badge>
                                            </Table.Td>


                                            <Table.Td>
                                                {row.beer}
                                            </Table.Td>


                                            <Table.Td>
                                                {row.water}
                                            </Table.Td>


                                            <Table.Td>
                                                {row.wine}
                                            </Table.Td>


                                            <Table.Td>
                                                <b>
                                                    {row.total}
                                                </b>
                                            </Table.Td>


                                        </Table.Tr>

                                    ))
                                }


                            </Table.Tbody>






                            <Table.Tfoot>


                                <Table.Tr>


                                    <Table.Th>
                                        TOTAL
                                    </Table.Th>


                                    <Table.Th />
                                    <Table.Th />
                                    <Table.Th />


                                    <Table.Th>
                                        {totals.beer}
                                    </Table.Th>


                                    <Table.Th>
                                        {totals.water}
                                    </Table.Th>


                                    <Table.Th>
                                        {totals.wine}
                                    </Table.Th>


                                    <Table.Th>
                                        {totals.total}
                                    </Table.Th>


                                </Table.Tr>


                            </Table.Tfoot>



                        </Table>


                    </Paper>


                }






                {
                    !loading &&
                    filteredOverview.length === 0 &&
                    eventId > 0 &&

                    <Paper
                        p="xl"
                        radius="md"
                        withBorder
                    >

                        <Text c="dimmed">
                            No drinks found for this selection.
                        </Text>

                    </Paper>

                }



            </Stack>


        </div>

    );
};

export default AdminDrinkOverview;